import React, { useEffect, useState, Fragment } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import IconLibrary from './IconLibrary';

function ListBoxSelectorWithSearch({
  height = '400px',
  getLeftItems = () => [],
  getRightItems = () => [],
  onItemsAdded = () => undefined,
  onItemsRemoved = () => undefined,
  leftTitle = null,
  rightTitle = null,
  parentId = null,
  onChange = () => undefined,
  searchPlaceholder = 'Search',
}) {
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async (leftPageNumber = 1, rightPageNumber = 1) => {
    const leftItems = await getLeftItems(leftPageNumber);
    const rightItems = await getRightItems(rightPageNumber);
    setLeft(leftItems);
    setRight(rightItems);
  };

  const handleToggle = (side, value) => {
    if (side === 'left') {
      setSelectedLeft(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value],
      );
    } else {
      setSelectedRight(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value],
      );
    }
  };

  const handleAllRight = async () => {
    if (!left?.data?.length) {
      return;
    }
    setIsSaving(true);
    setSelectedLeft([]);
    await onItemsAdded(left.data.map(item => item.value));
    await fetchData();
    setIsSaving(false);
  };

  const handleAllLeft = async () => {
    if (!right?.data?.length) {
      return;
    }
    setIsSaving(true);
    setSelectedRight([]);
    await onItemsRemoved(right.data.map(item => item.value));
    await fetchData();
    setIsSaving(false);
  };

  const handleCheckedRight = async () => {
    setIsSaving(true);
    await onItemsAdded(selectedLeft);
    await fetchData();
    setSelectedLeft([]);
    setIsSaving(false);
  };

  const handleCheckedLeft = async () => {
    setIsSaving(true);
    await onItemsRemoved(selectedRight);
    await fetchData();
    setSelectedRight([]);
    setIsSaving(false);
  };

  useEffect(() => {
    onChange(parentId, right);
  }, [parentId, right]);

  useEffect(() => {
    fetchData();
  }, [parentId]);

  const onSearch = async e => {
    const searchValue = e.target.value;
    if (searchValue.length > 1) {
      const getLeft = await getLeftItems(1, searchValue);
      setLeft(getLeft);
    } else if (searchValue.length === 0) {
      // refetch original data
      fetchData();
    }
  };

  const customList = (customItems, selected, side) => {
    let pageCount = customItems?.totalPages;

    if (customItems?.total) {
      pageCount = Math.ceil(customItems.total / customItems.limit);
    }
    return (
      <Box>
        <Box
          border={1}
          borderColor="divider"
          borderRadius={1}
          sx={{ height, overflow: 'auto' }}>
          <List dense component="div">
            {customItems?.data?.map((item, idx) => (
              <Fragment key={item.value?.id || item.value}>
                <ListItem dense disableGutters>
                  <ListItemButton
                    selected={selected.includes(item.value)}
                    alignItems="flex-start"
                    onClick={() => handleToggle(side, item.value)}>
                    <ListItemText
                      primary={
                        item.label +
                        (item.status === false ? ' (In-Active)' : '')
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {idx < (customItems?.data?.length || 0) - 1 && <Divider />}
              </Fragment>
            ))}
          </List>
        </Box>
        {pageCount > 1 && (
          <Pagination
            page={customItems?.page || 1}
            onChange={async (event, value) => {
              if (side === 'left') {
                const newItems = await getLeftItems(value);
                setLeft(newItems);
              } else {
                const newItems = await getRightItems(value);
                setRight(newItems);
              }
            }}
            sx={{
              mt: 1,
              mr: 1,
              ml: 1,
              justifyContent: 'flex-end',
              display: 'flex',
            }}
            count={pageCount}
            size="small"
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ marginTop: 2, marginBottom: 2 }}>
      {leftTitle && rightTitle && (
        <Grid2
          container
          spacing={2}
          textAlign="left"
          justifyContent="center"
          alignItems="center">
          <Grid2 md={5}>
            <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
              {leftTitle}
            </Typography>
          </Grid2>
          <Grid2 md={2}>&nbsp;</Grid2>
          <Grid2 md={5}>
            <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
              {rightTitle}
            </Typography>
          </Grid2>
        </Grid2>
      )}
      <Grid2 container spacing={2} justifyContent="center" alignItems="center">
        <Grid2 md={5}>
          <TextField
            onChange={onSearch}
            placeholder={searchPlaceholder}
            fullWidth
            size="small"
          />
          <Box sx={{ mt: 2 }}>{customList(left, selectedLeft, 'left')}</Box>
        </Grid2>
        <Grid2 md={2}>
          {isSaving ? (
            <Box mb={3}>
              <CircularProgress label="Saving" size={50} />
              <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                Saving
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={selectedLeft.length === 0 || isSaving}>
                <IconLibrary.MoveRight />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={!left?.data?.length || isSaving}>
                <IconLibrary.MoveAllRight />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={selectedRight.length === 0 || isSaving}>
                <IconLibrary.MoveLeft />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAllLeft}
                disabled={!right?.data?.length || isSaving}>
                <IconLibrary.MoveAllLeft />
              </Button>
            </Box>
          )}
        </Grid2>
        <Grid2 md={5} sx={{ mt: 2 }}>
          {customList(right, selectedRight, 'right')}
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default ListBoxSelectorWithSearch;
